import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "../shared/Footer";
import Navbar from "../shared/Navbar";
import Sidebar from "../shared/Sidebar";
import config from "../../config";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { ContextBoard } from "../../context/ContextBoard";
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Toastify
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function Class9Reports() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("secondaryAdminToken");
  const permissions = JSON.parse(sessionStorage.getItem("permissions") || "[]");
  const [loading, setLoading] = useState(false);

  // ---------- MODAL STATES ----------
  const [showDateModal, setShowDateModal] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/secondary-admin");
    }
  }, []);

  useEffect(() => {
    if (!permissions[4]?.caN_VIEW) {
      navigate("/secondary-admin-dashboard");
    }
  }, [permissions, navigate]);

  const reportOptions = [
    {
      label: "Rejected by Accounts",
      url: `${config.API_BASE_URL}/AdminGetFORejectedReportSecondary`,
      method: "GET",
      dataPath: `data["rejectedReport_list"]`,
      buttonClass: "btn-danger",
    },
    {
      label: "Approved by Accounts",
      url: `${config.API_BASE_URL}/AdminGetFOApprovedReportSecondary`,
      method: "GET",
      dataPath: `data["approvedReport_list"]`,
      buttonClass: "btn-success",
    },
    {
      label: "Pending at Accounts",
      url: `${config.API_BASE_URL}/AdminGetFOPendingReportSecondary`,
      method: "GET",
      dataPath: `data["pendingReport_list"]`,
      buttonClass: "btn-primary",
    },
    {
      label: "Payment not Done",
      url: `${config.API_BASE_URL}/AdminGetChallanNotPaidReportSecondary`,
      method: "GET",
      dataPath: `data["challanNotPaidReport_list"]`,
      buttonClass: "btn-info",
    },
    {
      label: "All Challan",
      url: `${config.API_BASE_URL}/AdminGetAllChallanReport_CL9`,
      method: "GET",
      dataPath: `data["allChallanReport_list"]`,
      buttonClass: "btn-warning",
    },
    {
      label: "Challan Not Generated",
      url: `${config.API_BASE_URL}/AdminGetChallanNotGenerateReport_CL9`,
      method: "GET",
      dataPath: `data["challanNotGenerateReport_list"]`,
      buttonClass: "btn-secondary",
    },
    {
      label: "District wise Student Count",
      url: `${config.API_BASE_URL}/ReportDistWiseStudentCount_CL9`,
      method: "POST",
      dataPath: `data["reportDistWiseCount_list"]`,
      buttonClass: "btn-dark",
    },
    {
      label: "District wise School wise Student Count",
      url: `${config.API_BASE_URL}/AdminDistrictSchoolCount_CL9NEW`,
      method: "POST", // For date filter
      dataPath: `data["adminDistrictCL9_list"]`,
      buttonClass: "btn-success",
    },
  ];

  // ---------------- FETCH WITHOUT DATE --------------------
  const fetchReportData = async (reportLabel) => {
    const reportConfig = reportOptions.find((r) => r.label === reportLabel);
    if (!reportConfig) {
      Swal.fire("Invalid report type.", "", "error");
      return;
    }

    if (!token) {
      Swal.fire("Auth Error", "Token is missing. Please login.", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(reportConfig.url, {
        method: reportConfig.method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      const extractedData =
        getValueFromPath(result, reportConfig.dataPath) || [];

      if (!extractedData.length) {
        Swal.fire("No data found.", "", "info");
        return;
      }

      generateExcel(extractedData, reportConfig.label);
    } catch (error) {
      Swal.fire("Error", "Error fetching report.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- FETCH WITH DATE (MODAL) --------------------
  const fetchReportWithDate = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select both dates!");
      return;
    }

    // 🚫 Prevent selecting To Date earlier than From Date
    if (new Date(toDate) < new Date(fromDate)) {
      toast.error("To Date cannot be earlier than From Date!");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(selectedReport.url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          FromDate: fromDate,
          ToDate: toDate,
        }),
      });

      const result = await response.json();
      const extractedData =
        getValueFromPath(result, selectedReport.dataPath) || [];

      if (!extractedData.length) {
        toast.error("No data found for selected date range");
        return;
      }

      generateExcel(extractedData, selectedReport.label);
      setShowDateModal(false);
    } catch (err) {
      toast.error("Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  const getValueFromPath = (obj, path) => {
    try {
      return path
        .replace(/\[(["']?)([^\[\]"']+)\1\]/g, ".$2")
        .replace(/^\./, "")
        .split(".")
        .reduce((acc, key) => acc?.[key], obj);
    } catch {
      return undefined;
    }
  };

  const generateExcel = (data, title) => {
    try {
      const headers = ["Sl. No.", ...Object.keys(data[0])];
      const formattedHeaders = [
        "SL. NO.",
        ...Object.keys(data[0]).map((k) => k.toUpperCase()),
      ];

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-${now.getFullYear()}  ${now
        .toLocaleTimeString("en-GB")
        .toUpperCase()}`;

      const ws = XLSX.utils.aoa_to_sheet([
        [`Report: ${title}`],
        [`Generated on: ${dateStr}`],
        [],
        formattedHeaders,
      ]);

      const rows = data.map((item, i) => [
        i + 1,
        ...headers.slice(1).map((key) => item[key] ?? ""),
      ]);

      XLSX.utils.sheet_add_aoa(ws, rows, { origin: -1 });

      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: formattedHeaders.length - 1 } },
      ];

      ws["!cols"] = formattedHeaders.map(() => ({ wch: 20 }));

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");

      const safeFileName = `Secondary_Report_CL9_${title.replace(
        /[^a-zA-Z0-9]/g,
        "_"
      )}.xlsx`;

      XLSX.writeFile(wb, safeFileName);

      Swal.fire("Success", "Excel file downloaded", "success");
    } catch (err) {
      Swal.fire("Error", "Failed to generate Excel", "error");
    }
  };
  const handleChangeFromDate = (date) => {
    if (date) {
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();

      setFromDate(`${dd}-${mm}-${yyyy}`);
    } else {
      setFromDate("");
    }
  };

  const handleChangeToDate = (date) => {
    if (date) {
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();

      setToDate(`${dd}-${mm}-${yyyy}`);
    } else {
      setToDate("");
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        style={{ zIndex: "100001" }}
      />
      <div className="container-scroller">
        {loading && (
          <div className="custom-loader-container">
            <div className="custom-loader"></div>
          </div>
        )}

        {/* -------------------- DATE MODAL -------------------- */}
        {showDateModal && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="modal-content"
              style={{
                background: "#fff",
                padding: "25px",
                borderRadius: "10px",
                width: "400px",
              }}
            >
              <h4>Select Date Range</h4>

              {/* ---------------- FROM DATE ---------------- */}
              <div className="form-group mt-3">
                <label>From Date</label>
                {/* <DatePicker
                  selected={fromDate ? new Date(fromDate) : null}
                  onChange={(date) =>
                    setFromDate(date ? date.toISOString().split("T")[0] : "")
                  }
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select From Date"
                  minDate={new Date("2025-01-01")}
                  maxDate={new Date()}
                /> */}
                <DatePicker
                  minDate={new Date(2025, 11, 18)}
                  maxDate={new Date()}
                  selected={
                    fromDate
                      ? (() => {
                        const [dd, mm, yyyy] = fromDate.split("-");
                        return new Date(
                          Number(yyyy),
                          Number(mm) - 1,
                          Number(dd)
                        );
                      })()
                      : null
                  }
                  onChange={(date) => handleChangeFromDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select From Date"
                  onKeyDown={(e) => e.preventDefault()}
                />
              </div>

              {/* ---------------- TO DATE ---------------- */}
              <div className="form-group mt-3">
                <label>To Date</label>
                {/* <DatePicker
                  selected={toDate ? new Date(toDate) : null}
                  onChange={(date) =>
                    setToDate(date ? date.toISOString().split("T")[0] : "")
                  }
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select To Date"
                  minDate={
                    fromDate ? new Date(fromDate) : new Date("2025-01-01")
                  }
                  maxDate={new Date()}
                /> */}
                <DatePicker
                  minDate={
                    fromDate
                      ? (() => {
                        const [dd, mm, yyyy] = fromDate.split("-");
                        return new Date(
                          Number(yyyy),
                          Number(mm) - 1,
                          Number(dd)
                        );
                      })()
                      : new Date(2025, 0, 1)
                  }
                  maxDate={new Date()}
                  selected={
                    toDate
                      ? (() => {
                        const [dd, mm, yyyy] = toDate.split("-");
                        return new Date(
                          Number(yyyy),
                          Number(mm) - 1,
                          Number(dd)
                        );
                      })()
                      : null
                  }
                  onChange={(date) => handleChangeToDate(date)}
                  dateFormat="dd-MM-yyyy"
                  className="form-control"
                  placeholderText="Select To Date"
                  onKeyDown={(e) => e.preventDefault()}
                />
              </div>

              <div className="mt-4 d-flex justify-content-between">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowDateModal(false);
                    setFromDate("");
                    setToDate("");
                  }}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-success"
                  onClick={fetchReportWithDate}
                >
                  Download
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        <Navbar />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <section className="content-header mb-4">
                <h1>
                  <i className="fa fa-university" /> Class IX Reports
                </h1>
                <ol className="breadcrumb">
                  <li>
                    <a
                      onClick={() => navigate("/secondary-admin-dashboard")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="fa fa-tachometer mr-2"></i> Home
                    </a>
                  </li>
                  <li className="active">Secondary Admin Reports Class IX</li>
                </ol>
              </section>

              <div className="row">
                <div className="col-md-4">
                  <div className="card">
                    <div className="card-header">
                      <h5>Class IX Reports</h5>
                    </div>
                    <div className="card-body">
                      <table className="table">
                        <tbody>
                          {reportOptions.map((report, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  className={`btn ${report.buttonClass} w-100`}
                                  style={{ textAlign: "left" }}
                                  onClick={() => {
                                    if (
                                      report.label ===
                                      "District wise School wise Student Count" ||
                                      report.label ===
                                      "District wise Student Count"
                                    ) {
                                      setSelectedReport(report);
                                      setShowDateModal(true);
                                    } else {
                                      fetchReportData(report.label);
                                    }
                                  }}
                                >
                                  <i className="fa fa-download excel-icon-rounded"></i>
                                  &nbsp;
                                  {report.label}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default Class9Reports;
